// Imposter Lobby Management
// Handles player management and Firebase interactions for Imposter game

import {
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDoc,
  onSnapshot,
  serverTimestamp,
  setDoc,
  updateDoc,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { ImposterStateDocument } from './logic';

export type ImposterPlayerStatus = 'waiting' | 'ready' | 'playing' | 'left';

export interface ImposterPlayerRecord {
  id: string;
  name: string;
  status: ImposterPlayerStatus;
  joinedAt: number;
  lastUpdatedAt?: number;
  isReady: boolean; // Ready to start game
  hasSubmittedClue: boolean; // For current round
  hasVoted: boolean; // For voting phase
  wantsToVote?: boolean; // Player wants to vote instead of another round (optional until they choose)
}

const playersCollection = (partyId: string) => 
  collection(db, 'parties', partyId, 'imposter_players');

const playerDoc = (partyId: string, playerId: string) => 
  doc(playersCollection(partyId), playerId);

const gameStateDoc = (partyId: string) => 
  doc(db, 'parties', partyId, 'imposter_game', 'state');

const timestampToMillis = (value: Timestamp | number | undefined) => {
  if (!value) return Date.now();
  if (typeof value === 'number') return value;
  return value.toMillis();
};

// Add or update a player in the lobby
export const upsertImposterPlayer = async (
  partyId: string,
  playerId: string,
  name: string,
  status: ImposterPlayerStatus = 'waiting'
): Promise<void> => {
  const ref = playerDoc(partyId, playerId);
  const snap = await getDoc(ref);
  
  const payload: Record<string, unknown> = {
    name: name.trim(),
    status,
    isReady: false,
    hasSubmittedClue: false,
    hasVoted: false,
    // Don't initialize wantsToVote - let it be undefined until player chooses
    lastUpdatedAt: serverTimestamp(),
  };
  
  if (!snap.exists()) {
    payload.joinedAt = serverTimestamp();
  }
  
  await setDoc(ref, payload, { merge: true });
};

// Remove a player from the lobby
export const removeImposterPlayer = async (
  partyId: string,
  playerId: string
): Promise<void> => {
  await deleteDoc(playerDoc(partyId, playerId));
};

// Update player status
export const updateImposterPlayerStatus = async (
  partyId: string,
  playerId: string,
  status: ImposterPlayerStatus
): Promise<void> => {
  await updateDoc(playerDoc(partyId, playerId), {
    status,
    lastUpdatedAt: serverTimestamp(),
  });
};

// Mark player as ready
export const setPlayerReady = async (
  partyId: string,
  playerId: string,
  isReady: boolean
): Promise<void> => {
  await updateDoc(playerDoc(partyId, playerId), {
    isReady,
    lastUpdatedAt: serverTimestamp(),
  });
};

// Mark that player has submitted a clue
export const markClueSubmitted = async (
  partyId: string,
  playerId: string
): Promise<void> => {
  await updateDoc(playerDoc(partyId, playerId), {
    hasSubmittedClue: true,
    lastUpdatedAt: serverTimestamp(),
  });
};

// Mark that player has voted
export const markPlayerVoted = async (
  partyId: string,
  playerId: string
): Promise<void> => {
  await updateDoc(playerDoc(partyId, playerId), {
    hasVoted: true,
    lastUpdatedAt: serverTimestamp(),
  });
};

// Reset all players' clue/vote status for new round
export const resetPlayerRoundStatus = async (
  partyId: string,
  playerIds: string[]
): Promise<void> => {
  const updates = playerIds.map(playerId =>
    updateDoc(playerDoc(partyId, playerId), {
      hasSubmittedClue: false,
      hasVoted: false,
      lastUpdatedAt: serverTimestamp(),
    })
  );
  await Promise.all(updates);
};

// Subscribe to player changes
export const subscribeToImposterPlayers = (
  partyId: string,
  callback: (players: ImposterPlayerRecord[]) => void
): (() => void) => {
  const q = collection(db, 'parties', partyId, 'imposter_players');
  
  return onSnapshot(q, snapshot => {
    const players: ImposterPlayerRecord[] = [];
    snapshot.forEach(doc => {
      const data = doc.data();
      players.push({
        id: doc.id,
        name: data.name || 'Anonymous',
        status: data.status || 'waiting',
        joinedAt: timestampToMillis(data.joinedAt),
        lastUpdatedAt: timestampToMillis(data.lastUpdatedAt),
        isReady: data.isReady || false,
        hasSubmittedClue: data.hasSubmittedClue || false,
        hasVoted: data.hasVoted || false,
        wantsToVote: data.wantsToVote || false,
      });
    });
    
    // Sort by join time
    players.sort((a, b) => a.joinedAt - b.joinedAt);
    callback(players);
  });
};

// Subscribe to game state
export const subscribeToImposterGameState = (
  partyId: string,
  callback: (gameState: ImposterStateDocument | null) => void
): (() => void) => {
  const ref = gameStateDoc(partyId);
  
  return onSnapshot(ref, snapshot => {
    if (!snapshot.exists()) {
      callback(null);
      return;
    }
    
    const data = snapshot.data() as ImposterStateDocument;
    callback(data);
  });
};

// Rename a player
export const renameImposterPlayer = async (
  partyId: string,
  playerId: string,
  newName: string
): Promise<void> => {
  await updateDoc(playerDoc(partyId, playerId), {
    name: newName.trim(),
    lastUpdatedAt: serverTimestamp(),
  });
};

// Leave the queue (soft delete)
export const leaveImposterQueue = async (
  partyId: string,
  playerId: string
): Promise<void> => {
  await updateDoc(playerDoc(partyId, playerId), {
    status: 'left',
    lastUpdatedAt: serverTimestamp(),
  });
};

// Clean up all players (admin function)
export const clearAllImposterPlayers = async (partyId: string): Promise<void> => {
  const q = collection(db, 'parties', partyId, 'imposter_players');
  const snapshot = await getDocs(q);
  const deletions = snapshot.docs.map(doc => deleteDoc(doc.ref));
  await Promise.all(deletions);
};

// Reset all player flags for new round
export const resetAllPlayerFlags = async (partyId: string): Promise<void> => {
  const q = collection(db, 'parties', partyId, 'imposter_players');
  const snapshot = await getDocs(q);
  const updates = snapshot.docs.map(doc => 
    updateDoc(doc.ref, {
      hasSubmittedClue: false,
      hasVoted: false,
      wantsToVote: deleteField(), // Remove the field so it's undefined again
      lastUpdatedAt: serverTimestamp(),
    })
  );
  await Promise.all(updates);
};

// Set player's vote preference (continue or vote now)
export const setPlayerVotePreference = async (
  partyId: string,
  playerId: string,
  wantsToVote: boolean
): Promise<void> => {
  await updateDoc(playerDoc(partyId, playerId), {
    wantsToVote,
    lastUpdatedAt: serverTimestamp(),
  });
};

// Import getDocs for cleanup
import { getDocs } from 'firebase/firestore';
